import React from 'react';

import {
    Checkbox,
    FormGroup,
    MenuItem,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminSettingsContainer from '../AdminSettingsContainer';
import StyledFormTitle from '../styles/form-title';
import SaveButton from '../components/SaveButton';
import UnauthorizedUserAccessSetting from './UnauthorizedUserAccessSetting';
import Select from '../../../components/forms/Select';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';
import UserAccountCreationSetting from './UserAccountCreationSetting';

const StyledAccordian = styled(Accordion)`
    margin-left: 10px;
    margin-bottom: 28px;
    width: 40rem;
`;

const StyledFormGroupContainer = styled.div`
    margin-left: 10px;
    margin-bottom: 28px;
`;

const StyledHelperText = styled(Typography)`
    color: ${(props) => props.theme.colors.text.secondary};
`;

const StyledSelect = styled(Select)`
    width: 400px;
`;

const AuthSettings = () => {
    const { adminSettings, setAdminSettings, isSavingSettingsData, isSavingDisabled, handleSaveSettings, school } =
        useContainer(AdminSettingsContainer);
    const { authSettings } = adminSettings;

    const { t } = useTranslation('admin_settings');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id || event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const renderZoomSetings = () => {
        if (school?.isTeams) return null;

        return (
            <>
                <StyledFormGroupContainer>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={authSettings.forcedAuthentication}
                                onChange={handleCheckboxChange}
                                id="forcedAuthentication"
                                name="forcedAuthentication"
                            />
                        }
                        label={t('auth.forcedAuthenticationControl')}
                    />
                    <StyledHelperText variant="body2">{t('auth.helperTextLine1')}</StyledHelperText>
                </StyledFormGroupContainer>

                <StyledFormGroupContainer>
                    <FormGroup>
                        <StyledSelect
                            name="autoVerify"
                            id="autoVerify"
                            value={authSettings.autoVerify}
                            label={t('auth.autoVerify')}
                            labelId="select-auto-verify"
                            onChange={handleChange}
                        >
                            <MenuItem value={'never'}>{t('auth.autoVerifyNever')}</MenuItem>
                            <MenuItem value={'optional'}>{t('auth.autoVerifyOptional')}</MenuItem>
                            <MenuItem value={'always'}>{t('auth.autoVerifyAlways')}</MenuItem>
                        </StyledSelect>
                    </FormGroup>
                </StyledFormGroupContainer>
            </>
        );
    };

    return (
        <>
            <StyledFormTitle>{t('auth.title')}</StyledFormTitle>

            {school?.isTeams && <UserAccountCreationSetting />}

            <StyledAccordian>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography>{t('auth.joining-class-session')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <UnauthorizedUserAccessSetting />
                </AccordionDetails>
            </StyledAccordian>

            {renderZoomSetings()}

            <StyledFormGroupContainer>
                <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />
            </StyledFormGroupContainer>
        </>
    );
};

export default AuthSettings;
